import { styled } from '@linaria/react'
import React, { forwardRef, SelectHTMLAttributes } from 'react'
import { colors } from '../styles/colors'
import { Typography } from './Typography'
import ArrowIcon from '../svgr/Chevron'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: React.ReactNode
  options: SelectOption[]
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  hasError?: boolean
  className?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      value,
      onChange,
      placeholder,
      hasError = false,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <SelectContainer className={className}>
        {label && (
          <Typography fontSize={12} fontWeight={400} font="roboto">
            {label}
          </Typography>
        )}

        <SelectWrapper hasError={hasError}>
          <SelectElement
            ref={ref}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectElement>

          <StyledArrowIcon />
        </SelectWrapper>
      </SelectContainer>
    )
  },
)

Select.displayName = 'Select'

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SelectWrapper = styled.div<{ hasError: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
`

const SelectElement = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid ${colors.borderPrimary};
  border-radius: 12px;
  background: ${colors.bgSecondary};
  color: ${colors.textPrimary};
  font-family: 'Manrope', sans-serif;
  font-size: 16px;
  font-weight: 400;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.borderFocus};
  }

  &:hover {
    border-color: ${colors.borderHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${colors.bgPrimary};
    color: ${colors.textPrimary};
    padding: 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 400;
  }
`

const StyledArrowIcon = styled(ArrowIcon)`
  --icon-size: 16px;
  position: absolute;
  right: 16px;
  top: calc(50% - var(--icon-size) / 2);
  width: var(--icon-size);
  height: var(--icon-size);
  pointer-events: none;
  transform: rotate(90deg);
`
